import { DateTime, WeekdayNumbers } from "luxon";
import { Calendar, CalendarRangeValue } from "../../Calendar/Calendar";
import { useMemo } from "react";
import { usePrevious } from "../../../internal";

export type DateFieldRangeCalendarProps = {
  startDate: DateTime | null;
  endDate: DateTime | null;
  onKeyDown: (event: React.KeyboardEvent<HTMLDivElement>) => void;
  onSelection: ({
    startDate,
    endDate,
  }: {
    startDate: DateTime | null;
    endDate: DateTime | null;
  }) => void;
  minDate?: DateTime | null;
  maxDate?: DateTime | null;
  unavailable?: {
    dates?: DateTime[];
    daysOfWeek?: WeekdayNumbers[];
  };
};

export const DateFieldRangeCalendar = ({
  startDate,
  endDate,
  onKeyDown,
  onSelection,
  minDate,
  maxDate,
  unavailable,
}: DateFieldRangeCalendarProps) => {
  const previousStartDate = usePrevious(startDate);
  const previousEndDate = usePrevious(endDate);

  const handleCalendarSelection = (data: { value: CalendarRangeValue }) => {
    if (!data.value) return;
    const calStartDate = data.value.start
      ? DateTime.fromISO(data.value.start, { zone: "utc" })
      : null;
    const calEndDate = data.value.end
      ? DateTime.fromISO(data.value.end, { zone: "utc" })
      : null;
    onSelection({
      startDate: calStartDate,
      endDate: calEndDate,
    });
  };

  const defaultFocusedDate = useMemo(() => {
    // Nothing selected, focus today
    if (!startDate && !endDate) return DateTime.now().toISODate();
    if (!startDate) return endDate?.toISODate();
    if (!endDate) return startDate?.toISODate();
    if (endDate && !previousEndDate?.equals(endDate)) {
      return endDate.toISODate();
    } else if (startDate && !previousStartDate?.equals(startDate)) {
      return startDate.toISODate();
    }

    if (endDate) return endDate.toISODate();
    if (startDate) return startDate.toISODate();
    return DateTime.now().toISODate();
  }, [previousStartDate, previousEndDate, startDate, endDate]);

  return (
    <Calendar
      // Crappy hack because the Calendar does not update when the value changes.
      key={`${startDate?.toISODate()}-${endDate?.toISODate()}`}
      range={true}
      onKeyDown={onKeyDown}
      defaultFocusedDate={defaultFocusedDate}
      value={{
        start: startDate?.toISODate() || undefined,
        end: endDate?.toISODate() || undefined,
      }}
      onSelection={handleCalendarSelection}
      defaultTimeZone="UTC"
      minDate={minDate?.toISODate() ?? undefined}
      maxDate={maxDate?.toISODate() ?? undefined}
      unavailable={
        unavailable
          ? {
              dates: unavailable.dates?.map((d) => d.toISODate() ?? ""),
              daysOfWeek: unavailable.daysOfWeek,
            }
          : undefined
      }
      _disableAutofocus
    />
  );
};
